import { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";

import useQuery from "hooks/useQuery";

import { StatsAggregationPeriodDto, StatsIntervalDto } from "services/stats/restModels";
import { dateToInputValue, endOfDay, inputValueToDate, startOfDay } from "shared/lib/dateInput";
import {
    buildStatsRange,
    defaultIntervalForRange,
    findStatsPeriodOption,
    STATS_INTERVAL_PERIOD_OPTIONS,
    STATS_PERIOD_OPTIONS,
    StatsPeriodKey,
    StatsRange,
} from "shared/lib/statsPeriods";

export interface StatisticsFiltersValue {
    menuId: number | null;
    period: StatsPeriodKey;
    range: StatsRange;
    interval: StatsIntervalDto;
}

export interface StatisticsFiltersPatch {
    menuId?: number;
    period?: StatsPeriodKey;
    range?: StatsRange;
    interval?: StatsIntervalDto;
}

interface StatisticsFiltersQuery {
    menuId?: string;
    period?: string;
    from?: string;
    to?: string;
    intervalAmount?: string;
    intervalPeriod?: string;
}

const DEFAULT_PERIOD: StatsPeriodKey = "today";

const parseNumber = (raw: string | undefined): number | null => {
    // Пустое значение отбрасываем до Number: у него пустая строка это 0, и `?menuId=`
    // превратился бы в запрос по несуществующему меню.
    if (raw === undefined || raw.trim() === "") return null;

    const parsed = Number(raw);

    return isNaN(parsed) ? null : parsed;
};

/*
* Разбор терпимый: неизвестный пресет или единица детализации из правленого руками URL
* заменяются значением по умолчанию, а не уходят в запрос — бэк на них отвечает 400, и экран
* показывал бы «не удалось загрузить» вместо очевидного состояния.
* */
const parsePeriod = (raw: string | undefined): StatsPeriodKey => {
    if (raw === "custom") return "custom";

    const found = STATS_PERIOD_OPTIONS.find((option) => option.key === raw);

    return found ? found.key : DEFAULT_PERIOD;
};

const parseIntervalPeriod = (raw: string | undefined): StatsAggregationPeriodDto | null => {
    const found = STATS_INTERVAL_PERIOD_OPTIONS.find((option) => option.value === raw);

    return found ? found.value : null;
};

/*
* Фильтры живут в query, а не в стейте фичи: локальный стейт умирает вместе с экраном, и
* возврат на статистику открывал бы её заново за сегодня. Заодно выборка переживает
* перезагрузку, а ссылку на неё можно отправить.
*
* Пишем через `replace` (как useActiveTab с ?tab=) — иначе «назад» браузера отменял бы правки
* фильтра по одной. Значения по умолчанию в строку не пишем, чтобы URL не разрастался.
* */
export const useStatisticsFilters = () => {
    const history = useHistory();
    const query = useQuery<StatisticsFiltersQuery>();

    const {
        menuId: rawMenuId,
        period: rawPeriod,
        from: rawFrom,
        to: rawTo,
        intervalAmount: rawIntervalAmount,
        intervalPeriod: rawIntervalPeriod,
    } = query;

    const value = useMemo<StatisticsFiltersValue>(() => {
        const period = parsePeriod(rawPeriod);
        const option = findStatsPeriodOption(period);

        const customFrom = rawFrom === undefined ? null : inputValueToDate(rawFrom);
        const customTo = rawTo === undefined ? null : inputValueToDate(rawTo);

        const range =
            period === "custom"
                ? customRange(customFrom, customTo)
                : buildStatsRange(period, new Date());

        const intervalAmount = parseNumber(rawIntervalAmount);
        const intervalPeriod = parseIntervalPeriod(rawIntervalPeriod);

        const defaultInterval = period === "custom" ? defaultIntervalForRange(range) : option.interval;

        return {
            menuId: parseNumber(rawMenuId),
            period,
            range,
            interval:
                intervalAmount === null || intervalAmount < 1 || intervalPeriod === null
                    ? defaultInterval
                    : { amount: Math.floor(intervalAmount), period: intervalPeriod },
        };
    }, [rawMenuId, rawPeriod, rawFrom, rawTo, rawIntervalAmount, rawIntervalPeriod]);

    const patch = useCallback(
        (next: StatisticsFiltersPatch) => {
            const merged = { ...value, ...next };

            /*
            * Смена периода возвращает детализацию к его собственной: шаг в час, разумный для
            * суток, на годовом периоде превращается в тысячи точек и упирается в отказ бэка.
            * */
            /*
            * Любая правка периода — и пресета, и границ произвольного — возвращает детализацию к
            * его собственной. Сохранять выбранный руками шаг заманчиво, но именно так экран
            * залипает: часовой шаг, разумный для суток, после растягивания периода до года даёт
            * почти девять тысяч точек, бэк отвечает отказом, а шаг к тому моменту уже записан в
            * ссылку — и вернуться из этого состояния нечем.
            * */
            const interval =
                next.interval === undefined && (next.period !== undefined || next.range !== undefined)
                    ? defaultIntervalFor(merged.period, merged.range)
                    : merged.interval;

            const params = new URLSearchParams();

            if (merged.menuId !== null) params.set("menuId", String(merged.menuId));
            if (merged.period !== DEFAULT_PERIOD) params.set("period", merged.period);

            // Даты нужны только произвольному периоду: у пресета они считаются из него самого,
            // и записанные в строку разъехались бы с ним на следующий день.
            if (merged.period === "custom") {
                params.set("from", dateToInputValue(merged.range.from));
                params.set("to", dateToInputValue(merged.range.to));
            }

            const defaultInterval = defaultIntervalFor(merged.period, merged.range);

            // Детализация в строке — только когда её меняли руками.
            if (
                interval.amount !== defaultInterval.amount ||
                interval.period !== defaultInterval.period
            ) {
                params.set("intervalAmount", String(interval.amount));
                params.set("intervalPeriod", interval.period);
            }

            history.replace({ search: params.toString() });
        },
        [history, value]
    );

    return { value, patch };
};

const defaultIntervalFor = (period: StatsPeriodKey, range: StatsRange): StatsIntervalDto =>
    period === "custom" ? defaultIntervalForRange(range) : findStatsPeriodOption(period).interval;

/*
* Границы произвольного периода — календарные дни: недописанную дату не применяем (иначе
* период уезжает в нулевой год прямо во время набора), перевёрнутую чиним на месте.
* */
const customRange = (from: Date | null, to: Date | null): StatsRange => {
    const today = new Date();
    const start = startOfDay(from || today);
    const end = endOfDay(to === null || to.getTime() < start.getTime() ? start : to);

    return { from: start, to: end };
};
